import React, { useState } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import SEO from '../components/SEO';
import AdminLoginGate from '../components/AdminLoginGate';
import AdminTabs from '../components/AdminTabs';
import { API, authHeaders, useAdminAuth } from '../lib/adminAuth';
import { Shield, ShieldCheck, KeyRound } from 'lucide-react';

function ChangePasswordCard({ token }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(''); setSuccess('');
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: current, new_password: next }, { headers: authHeaders(token) });
      setSuccess('Password changed.');
      setCurrent(''); setNext('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to change password.');
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-zinc-200 p-6" data-testid="profile-change-password">
      <div className="flex items-center gap-2 mb-4">
        <KeyRound className="w-5 h-5 text-[#F0A500]" />
        <h2 className="text-lg font-bold text-[#0A0A0A]">Change password</h2>
      </div>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="current-pw" className="text-xs uppercase tracking-wider font-bold text-[#52525B]">Current password</Label>
          <Input id="current-pw" data-testid="profile-current-password" type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="new-pw" className="text-xs uppercase tracking-wider font-bold text-[#52525B]">New password (at least 10 characters)</Label>
          <Input id="new-pw" data-testid="profile-new-password" type="password" required minLength={10} value={next} onChange={(e) => setNext(e.target.value)} />
        </div>
        {error && <p data-testid="profile-change-password-error" className="text-sm text-red-600">{error}</p>}
        {success && <p data-testid="profile-change-password-success" className="text-sm text-emerald-700 font-semibold">{success}</p>}
        <button type="submit" disabled={busy} data-testid="profile-change-password-btn" className="px-5 py-2 bg-[#F0A500] hover:bg-[#d99400] disabled:opacity-60 text-[#0A0A0A] font-bold uppercase tracking-wider text-xs">
          {busy ? 'Saving…' : 'Save password'}
        </button>
      </form>
    </div>
  );
}

function TotpCard({ token, me, refresh }) {
  const [step, setStep] = useState('idle'); // idle | enrolling | confirming | disabling
  const [data, setData] = useState(null); // { provisioning_uri, secret }
  const [code, setCode] = useState('');
  const [pw, setPw] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const begin = async () => {
    setBusy(true); setError('');
    try {
      const { data } = await axios.post(`${API}/auth/totp/begin`, {}, { headers: authHeaders(token) });
      setData(data);
      setStep('confirming');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start setup.');
    } finally { setBusy(false); }
  };

  const confirm = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      await axios.post(`${API}/auth/totp/confirm`, { code }, { headers: authHeaders(token) });
      setStep('idle'); setData(null); setCode('');
      refresh();
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid code.');
    } finally { setBusy(false); }
  };

  const disable = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      await axios.post(`${API}/auth/totp/disable`, { current_password: pw, new_password: '' }, { headers: authHeaders(token) });
      setStep('idle'); setPw('');
      refresh();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to disable two-factor.');
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-zinc-200 p-6" data-testid="profile-totp-card">
      <div className="flex items-center gap-2 mb-1">
        {me.totp_enabled
          ? <ShieldCheck className="w-5 h-5 text-emerald-600" />
          : <Shield className="w-5 h-5 text-[#F0A500]" />
        }
        <h2 className="text-lg font-bold text-[#0A0A0A]">Two-factor authentication</h2>
      </div>
      <p className="text-sm text-[#52525B] mb-5">
        {me.totp_enabled
          ? <>Two-factor is <strong className="text-emerald-700">enabled</strong> on your account. Each login will ask for a 6-digit code from your authenticator app after your password.</>
          : 'Add an extra layer of security by requiring a 6-digit code from an authenticator app (Google Authenticator, Authy, 1Password, etc.) at sign-in.'
        }
      </p>

      {step === 'idle' && !me.totp_enabled && (
        <button type="button" onClick={begin} disabled={busy} data-testid="profile-totp-begin-btn" className="px-5 py-2 bg-[#F0A500] hover:bg-[#d99400] disabled:opacity-60 text-[#0A0A0A] font-bold uppercase tracking-wider text-xs">
          {busy ? 'Starting…' : 'Enable two-factor'}
        </button>
      )}

      {step === 'idle' && me.totp_enabled && (
        <button type="button" onClick={() => setStep('disabling')} data-testid="profile-totp-disable-prompt-btn" className="px-5 py-2 bg-zinc-200 hover:bg-zinc-300 text-[#0A0A0A] font-bold uppercase tracking-wider text-xs">
          Disable two-factor
        </button>
      )}

      {step === 'confirming' && data && (
        <form onSubmit={confirm} className="space-y-5" data-testid="profile-totp-confirm-form">
          <div>
            <p className="text-sm text-[#52525B] mb-3">1. Open your authenticator app and scan this QR code:</p>
            <div className="bg-white p-4 inline-block border border-zinc-300">
              <QRCodeSVG value={data.provisioning_uri} size={192} level="M" />
            </div>
            <p className="text-xs text-[#52525B] mt-3">
              Can&rsquo;t scan? Enter this secret manually:{' '}
              <code className="bg-zinc-100 px-1.5 py-0.5 text-[#0A0A0A] font-mono break-all" data-testid="profile-totp-secret">{data.secret}</code>
            </p>
          </div>
          <div>
            <Label htmlFor="totp-confirm-code" className="text-xs uppercase tracking-wider font-bold text-[#52525B]">2. Enter the 6-digit code your app displays</Label>
            <Input
              id="totp-confirm-code"
              data-testid="profile-totp-code-input"
              required
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              className="text-center text-2xl tracking-[0.5em] font-mono max-w-xs"
              placeholder="••••••"
              autoFocus
            />
          </div>
          {error && <p data-testid="profile-totp-confirm-error" className="text-sm text-red-600">{error}</p>}
          <div className="flex gap-3">
            <button type="submit" disabled={busy} data-testid="profile-totp-confirm-btn" className="px-5 py-2 bg-[#F0A500] hover:bg-[#d99400] disabled:opacity-60 text-[#0A0A0A] font-bold uppercase tracking-wider text-xs">
              {busy ? 'Verifying…' : 'Enable'}
            </button>
            <button type="button" onClick={() => { setStep('idle'); setData(null); setError(''); }} className="px-5 py-2 bg-zinc-200 hover:bg-zinc-300 text-[#0A0A0A] font-semibold text-xs uppercase tracking-wider">Cancel</button>
          </div>
        </form>
      )}

      {step === 'disabling' && (
        <form onSubmit={disable} className="space-y-4" data-testid="profile-totp-disable-form">
          <p className="text-sm text-[#52525B]">Confirm your password to disable two-factor.</p>
          <Input
            data-testid="profile-totp-disable-pw"
            type="password"
            required
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder="Current password"
            autoFocus
          />
          {error && <p data-testid="profile-totp-disable-error" className="text-sm text-red-600">{error}</p>}
          <div className="flex gap-3">
            <button type="submit" disabled={busy} data-testid="profile-totp-disable-confirm-btn" className="px-5 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white font-bold uppercase tracking-wider text-xs">
              {busy ? 'Disabling…' : 'Disable two-factor'}
            </button>
            <button type="button" onClick={() => { setStep('idle'); setPw(''); setError(''); }} className="px-5 py-2 bg-zinc-200 hover:bg-zinc-300 text-[#0A0A0A] font-semibold text-xs uppercase tracking-wider">Cancel</button>
          </div>
        </form>
      )}
    </div>
  );
}

export default function AdminProfilePage() {
  const { token, me, checking, loginSuccess, logout, setMe } = useAdminAuth();
  const refresh = async () => {
    const { data } = await axios.get(`${API}/auth/me`, { headers: authHeaders(token) });
    setMe(data);
  };
  if (checking) return <div className="min-h-[70vh] flex items-center justify-center text-zinc-500">Checking session…</div>;
  if (!token || !me) {
    return (
      <div data-testid="admin-profile-page">
        <SEO title="Admin Sign-in" description="Restricted admin area." path="/admin/profile" noIndex />
        <AdminLoginGate title="Admin Sign-in" subtitle="Sign in to manage your admin profile." onLoggedIn={loginSuccess} />
      </div>
    );
  }
  return (
    <div data-testid="admin-profile-page">
      <SEO title="Admin — My Profile" description="Restricted admin area." path="/admin/profile" noIndex />
      <AdminTabs current="profile" me={me} onLogout={logout} />
      <div className="bg-[#F4F4F5] min-h-screen py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <div className="mb-8">
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#F0A500] block mb-2">Admin Dashboard</span>
            <h1 className="text-3xl sm:text-4xl font-bold text-[#0A0A0A]">My Profile</h1>
            <p className="text-sm text-[#52525B] mt-2">
              Signed in as <strong className="text-[#0A0A0A]">{me.username}</strong> ({me.role})
            </p>
          </div>
          <div className="space-y-6">
            <TotpCard token={token} me={me} refresh={refresh} />
            <ChangePasswordCard token={token} />
          </div>
        </div>
      </div>
    </div>
  );
}
